import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import { useAuth } from "react-oidc-context";
import { getOrgProfile } from "../dataAccess/apiService";
import { Organization } from "@clients/velys-infrastructure/api";

export interface IOrgOption {
  id: string;
  name: string;
  logo?: string;
}

export function useGetAllOrgs() {
  const user_profile = useAuthStore((state) => state.user_profile);
  const auth = useAuth();
  const [orgOptions, setOrgOptions] = useState<IOrgOption[]>([]);

  const orgIds = user_profile?.orgid ?? [];

  // Fetch all organizations
  const orgQueries = useQuery({
    queryKey: ["get-all-orgs", orgIds],
    queryFn: async () => {
      if (!orgIds || orgIds.length === 0) {
        return [];
      }

      // Fetch all organizations in parallel
      const orgPromises = orgIds.map((orgId) =>
        getOrgProfile(orgId).catch((error) => {
          console.error(`Failed to fetch org ${orgId}:`, error);
          return null;
        })
      );

      const results = await Promise.all(orgPromises);
      return results.filter((org): org is Organization => org !== null);
    },
    enabled: !!orgIds.length && auth.isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (orgQueries.data) {
      const options: IOrgOption[] = orgQueries.data.map((org) => ({
        id: org.id || "",
        name: org.name || org.id || "Unknown Organization",
        logo: org.logos?.["icon"] ? org.logos["icon"] : undefined,
      }));
      setOrgOptions(options);
    }
  }, [orgQueries.data]);

  return {
    organizations: orgQueries.data ?? [],
    orgOptions,
    isLoading: orgQueries.isLoading,
    isError: orgQueries.isError,
    error: orgQueries.error,
  };
}
